// S-AI 集客：工務店の LINE 公式アカウントの連携と、施主とのトーク
import { request2 } from '@/utils/httpClient'

export const fetchSaiLineChannel = () => {
  return request2({ url: '/sai/line/channel', method: 'GET' })
}

export const saveSaiLineChannel = (data) => {
  return request2({ url: '/sai/line/channel', method: 'POST', data })
}

/** 新着をメールで知らせるか */
export const saveSaiLineNotify = (enabled) => {
  return request2({ url: '/sai/line/channel/notify', method: 'POST', data: { enabled } })
}

export const fetchSaiLineConversations = (params = {}) => {
  return request2({ url: '/sai/line/conversations', method: 'GET', params })
}

/** params: { beforeId } で古い方へ、{ afterId } で新しい分だけ */
export const fetchSaiLineMessages = (contactId, params = {}) => {
  return request2({ url: `/sai/line/conversations/${contactId}/messages`, method: 'GET', params })
}

export const markSaiLineRead = (contactId, messageId) => {
  return request2({
    url: `/sai/line/conversations/${contactId}/read`,
    method: 'POST',
    data: messageId ? { messageId } : {},
  })
}

export const sendSaiLineMessage = (contactId, text) => {
  return request2({ url: `/sai/line/conversations/${contactId}/messages`, method: 'POST', data: { text } })
}

/** 施主が送った画像などの中身（blob） */
export const fetchSaiLineMessageContent = (messageId) => {
  return request2({ url: `/sai/line/messages/${messageId}/content`, method: 'GET', responseType: 'blob' })
}

// ---------------------------------------------------------------- 営業：施主カルテ

export const fetchSaiCartes = (params = {}) => {
  return request2({ url: '/sai/cartes', method: 'GET', params })
}

/** 営業の状況（ステータス別の件数・今後 7 日の面談・月ごとの新しいカルテ） */
export const fetchSaiCarteSummary = () => {
  return request2({ url: '/sai/cartes/summary', method: 'GET' })
}

export const fetchSaiCarte = (id) => {
  return request2({ url: `/sai/cartes/${id}`, method: 'GET' })
}

export const createSaiCarte = (data) => {
  return request2({ url: '/sai/cartes', method: 'POST', data })
}

export const updateSaiCarte = (id, data) => {
  return request2({ url: `/sai/cartes/${id}`, method: 'PUT', data })
}

export const deleteSaiCarte = (id) => {
  return request2({ url: `/sai/cartes/${id}`, method: 'DELETE' })
}

/** 集客の LINE 友だちからカルテを作る（あればそれを返す） */
export const createSaiCarteFromLine = (contactId) => {
  return request2({ url: `/sai/cartes/from-line/${contactId}`, method: 'POST' })
}

/** 面談準備シート用：カルテにつながる LINE トークの最近のメッセージ */
export const fetchSaiCarteLineMessages = (id, params = {}) => {
  return request2({ url: `/sai/cartes/${id}/line-messages`, method: 'GET', params })
}

// ---------------------------------------------------------------- 積算：草図と住宅商品からモデル・数量・概算金額

export const fetchSaiEstimates = (params = {}) => {
  return request2({ url: '/sai/estimates', method: 'GET', params })
}

export const fetchSaiEstimate = (id) => {
  return request2({ url: `/sai/estimates/${id}`, method: 'GET' })
}

/** 積算を作る（モデルを置く AI real CG のプロジェクトも作られる） */
export const createSaiEstimate = (data) => {
  return request2({ url: '/sai/estimates', method: 'POST', data })
}

export const updateSaiEstimate = (id, data) => {
  return request2({ url: `/sai/estimates/${id}`, method: 'PUT', data })
}

export const deleteSaiEstimate = (id) => {
  return request2({ url: `/sai/estimates/${id}`, method: 'DELETE' })
}

/** 現行比較：単価の根拠の資料（PDF・画像）を置く。返すのは { key, name } */
export const uploadSaiBasisFile = (file) => {
  const form = new FormData()
  form.append('file', file)
  return request2({ url: '/sai/estimates/basis-files', method: 'POST', data: form, timeout: 120000 })
}

/** 管理画面：現行比較の B のモデルに選べる積算（全アカウント） */
export const fetchSaiEstimateModelChoices = () => {
  return request2({ url: '/sai/estimates/model-choices', method: 'GET' })
}

export const fetchSaiEstimateModelChoice = (id) => {
  return request2({ url: `/sai/estimates/model-choices/${id}`, method: 'GET' })
}

/**
 * AI の読み取りの作業（受け付け → 結果を取りに行く）。読み取りは 20〜60 秒かかり、前段の proxy の 60 秒を超えることがあるため。
 * 戻り値は通常の応答と同じ形 { status, msg, data }（data は読み取りの結果）
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
async function runSaiReadJob(path, file, { intervalMs = 2000, maxMs = 300000 } = {}) {
  const form = new FormData()
  form.append('file', file)
  const started = await request2({ url: path, method: 'POST', data: form, timeout: 120000 })
  if (!started || Number(started.status) !== 200 || !started.data || !started.data.jobId) return started
  const t0 = Date.now()
  while (Date.now() - t0 < maxMs) {
    await sleep(intervalMs)
    const res = await request2({ url: `${path}/${started.data.jobId}`, method: 'GET' })
    if (!res || Number(res.status) !== 200 || !res.data) {
      // 一時的に取れないだけなら次で
      if (res && Number(res.status) === 500 && res.msg && /見つかりません/.test(res.msg)) return res
      continue
    }
    if (res.data.state === 'done') return { status: 200, msg: 'ok', data: res.data.result }
    if (res.data.state === 'failed') return { status: 500, msg: res.data.error || '読み取りに失敗しました。', data: null }
  }
  return { status: 500, msg: '読み取りに時間がかかっています。もう一度お試しください。', data: null }
}

/** 現行比較：建築図面（PDF）のページを AI で仕分ける（1〜3 階の平面図・立面図。ファイルは保存されない） */
export const classifySaiEstimatePages = (file) => runSaiReadJob('/sai/estimates/classify-pages', file)

/** 現行比較：現行積算書（PDF・画像・Excel）を AI で読む（ファイルは保存されない） */
export const readSaiEstimateSheet = (file) => runSaiReadJob('/sai/estimates/read-sheet', file)

/** 積算に使う住宅商品と単価（既定値。未設定は null） */
export const fetchSaiEstimateConfig = () => {
  return request2({ url: '/sai/estimates/config', method: 'GET' })
}

/** 管理画面（案件管理）：住宅商品・単価の既定値を保存する。送った項目だけ上書き */
export const saveSaiEstimateConfig = (data) => {
  return request2({ url: '/sai/estimates/config', method: 'PUT', data })
}

// ---------------------------------------------------------------- AI 応答（審査済みの資料だけで答える）

export const fetchSaiAiDocs = () => {
  return request2({ url: '/sai/ai/docs', method: 'GET' })
}

export const fetchSaiAiDoc = (id) => {
  return request2({ url: `/sai/ai/docs/${id}`, method: 'GET' })
}

export const createSaiAiDoc = (data) => {
  return request2({ url: '/sai/ai/docs', method: 'POST', data })
}

export const updateSaiAiDoc = (id, data) => {
  return request2({ url: `/sai/ai/docs/${id}`, method: 'PUT', data })
}

export const deleteSaiAiDoc = (id) => {
  return request2({ url: `/sai/ai/docs/${id}`, method: 'DELETE' })
}

/** 画面で見ていた版（version）を送る。ほかで変更されていれば断られる */
export const approveSaiAiDoc = (id, version) => {
  return request2({ url: `/sai/ai/docs/${id}/approve`, method: 'POST', data: { version } })
}

export const unapproveSaiAiDoc = (id) => {
  return request2({ url: `/sai/ai/docs/${id}/unapprove`, method: 'POST' })
}

/** .txt / .md / .pdf / .doc / .docx から未審査の資料を作る */
export const importSaiAiDoc = (file, title) => {
  const form = new FormData()
  form.append('file', file)
  if (title) form.append('title', title)
  return request2({ url: '/sai/ai/docs/import', method: 'POST', data: form, timeout: 120000 })
}

export const askSaiAi = (question) => {
  return request2({ url: '/sai/ai/ask', method: 'POST', data: { question }, timeout: 60000 })
}

export const fetchSaiAiConfig = () => {
  return request2({ url: '/sai/ai/config', method: 'GET' })
}

export const saveSaiAiConfig = (data) => {
  return request2({ url: '/sai/ai/config', method: 'PUT', data })
}

export const fetchSaiAiLogs = (params = {}) => {
  return request2({ url: '/sai/ai/logs', method: 'GET', params })
}

// ---------------------------------------------------------------- AI 相談（S-AI のホーム：AI に依頼して成果物を作る）

/** その人の会話一覧（状態つき）・最初の依頼の例・審査済み資料の数。module を省くと全部 */
export const fetchSaiConsultThreads = (module) => {
  return request2({ url: '/sai/consult/threads', method: 'GET', params: module ? { module } : {} })
}

/** 会話の発言（成果物のカードつき）と、右の画面に出す成果物 */
export const fetchSaiConsultThread = (id) => {
  return request2({ url: `/sai/consult/threads/${id}`, method: 'GET' })
}

/** Make House からのお知らせ（S-AI のホーム）→ { news, canEdit }。書くのはプラットフォーム管理者だけ */
export const fetchSaiNews = (limit = 10) => {
  return request2({ url: '/sai/news', method: 'GET', params: { limit } })
}

/** { id?, title, body, linkUrl, publishedAt（ミリ秒） }。id が無ければ新しく書く */
export const saveSaiNews = (news) => {
  const data = { title: news.title, body: news.body, linkUrl: news.linkUrl, publishedAt: news.publishedAt }
  return news.id
    ? request2({ url: `/sai/news/${news.id}`, method: 'PUT', data })
    : request2({ url: '/sai/news', method: 'POST', data })
}

export const deleteSaiNews = (id) => {
  return request2({ url: `/sai/news/${id}`, method: 'DELETE' })
}

/** AI が覚えたこと（アカウントごと、会話をまたいで使う） */
export const fetchSaiMemories = () => {
  return request2({ url: '/sai/consult/memories', method: 'GET' })
}

// ---------------------------------------------------------------- 定時タスク（本人のもの）

export const fetchSaiTasks = () => {
  return request2({ url: '/sai/consult/tasks', method: 'GET' })
}

/** body: { name, prompt, module, scheduleType, weekday?, monthDay?, runDate?, runTime, notifyEmail, enabled } */
export const createSaiTask = (body) => {
  return request2({ url: '/sai/consult/tasks', method: 'POST', data: body })
}

export const updateSaiTask = (id, body) => {
  return request2({ url: `/sai/consult/tasks/${id}`, method: 'PUT', data: body })
}

export const setSaiTaskEnabled = (id, enabled) => {
  return request2({ url: `/sai/consult/tasks/${id}/enabled`, method: 'PUT', data: { enabled } })
}

export const deleteSaiTask = (id) => {
  return request2({ url: `/sai/consult/tasks/${id}`, method: 'DELETE' })
}

/** 今すぐ 1 回実行（予定はそのまま） */
export const runSaiTask = (id) => {
  return request2({ url: `/sai/consult/tasks/${id}/run`, method: 'POST' })
}

export const updateSaiMemory = (id, content) => {
  return request2({ url: `/sai/consult/memories/${id}`, method: 'PUT', data: { content } })
}

/** 忘れる（「覚えました」の行も取り消し済みになる） */
export const deleteSaiMemory = (id) => {
  return request2({ url: `/sai/consult/memories/${id}`, method: 'DELETE' })
}

/** 会話に星を付ける・外す（付けた会話は一覧の上） */
export const pinSaiConsultThread = (id, pinned) => {
  return request2({ url: `/sai/consult/threads/${id}/pin`, method: 'PUT', data: { pinned: Boolean(pinned) } })
}

export const deleteSaiConsultThread = (id) => {
  return request2({ url: `/sai/consult/threads/${id}`, method: 'DELETE' })
}

/**
 * 依頼を送る（threadId が無ければ新しい会話）。AI は後ろで答えるので、answer は status=PENDING で返る。
 * attachments は添付したファイルの名前、attachmentIds は先に上げた添付（AI が中身を読む）、
 * products は概算積算で選べる住宅商品の名前
 */
export const sendSaiConsultMessage = ({ module, threadId, text, attachments, attachmentIds, products }) => {
  return request2({
    url: '/sai/consult/messages',
    method: 'POST',
    data: { module, threadId, text, attachments, attachmentIds, products },
    timeout: 30000,
  })
}

/** 添付を上げる（送る前に 1 つずつ）。読み取りはサーバーの後ろで始まり、{ id, fileName, status } が返る */
export const uploadSaiConsultFile = (file) => {
  const form = new FormData()
  form.append('file', file)
  return request2({ url: '/sai/consult/files', method: 'POST', data: form, timeout: 120000 })
}

/** AI が提案した概算積算を、右の画面で確認して始めた（作った積算の id をつなぐ） */
export const startSaiConsultCard = (cardId, refId) => {
  return request2({ url: `/sai/consult/cards/${cardId}/start`, method: 'POST', data: { refId } })
}

/** 1 つの回答の処理の記録（本人の会話だけ） */
export const fetchSaiConsultTrace = (threadId, messageId) => {
  return request2({ url: `/sai/consult/threads/${threadId}/trace`, method: 'GET', params: { messageId } })
}

/** AI が作ったファイルの中身（本人の会話のものだけ。ファイルは画面で作る） */
export const fetchSaiConsultOutput = (id) => {
  return request2({ url: `/sai/consult/outputs/${id}`, method: 'GET' })
}

/** AI が作ったファイルを文字の PDF に（サーバーの Chromium）。body: { html, footer? }。返りは blob（使えなければ 503） */
export const renderSaiConsultOutputPdf = (id, body) => {
  return request2({ url: `/sai/consult/outputs/${id}/pdf`, method: 'POST', data: body, responseType: 'blob', timeout: 90000 })
}

/** 処理の記録の全体の集計（プラットフォーム管理者だけ・中身なし） */
export const fetchSaiConsultTraceStats = (days) => {
  return request2({ url: '/sai/consult/trace/stats', method: 'GET', params: { days } })
}

/** 原理資料（MH 共通）：module=all で全部、6 モジュールのどれかならそれと共通 */
export const fetchSaiConsultDocs = (module) => {
  return request2({ url: '/sai/consult/docs', method: 'GET', params: { module } })
}

export const fetchSaiConsultDoc = (id) => {
  return request2({ url: `/sai/consult/docs/${id}`, method: 'GET' })
}

export const createSaiConsultDoc = (data) => {
  return request2({ url: '/sai/consult/docs', method: 'POST', data })
}

export const updateSaiConsultDoc = (id, data) => {
  return request2({ url: `/sai/consult/docs/${id}`, method: 'PUT', data })
}

export const deleteSaiConsultDoc = (id) => {
  return request2({ url: `/sai/consult/docs/${id}`, method: 'DELETE' })
}

export const approveSaiConsultDoc = (id, version) => {
  return request2({ url: `/sai/consult/docs/${id}/approve`, method: 'POST', data: { version } })
}

export const unapproveSaiConsultDoc = (id) => {
  return request2({ url: `/sai/consult/docs/${id}/unapprove`, method: 'POST' })
}

export const importSaiConsultDoc = (module, file, title) => {
  const form = new FormData()
  form.append('module', module)
  form.append('file', file)
  if (title) form.append('title', title)
  return request2({ url: '/sai/consult/docs/import', method: 'POST', data: form, timeout: 120000 })
}
